import { Scrollbar } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import {
  ConversationDirection,
  ConversationsChannels,
  ConversationStatus,
} from 'lib-shared-legacy-types';
import * as React from 'react';

import styles from './index.module.scss';
import { TimelineItem } from './timelineItem';

export type TimelineConversationType = {
  channel: ConversationsChannels;
  date: string;
  hasFollowUp: boolean;
  id: number;
  isCurrent: boolean;
  is_closed: boolean;
  status: ConversationStatus;
  title: string;
  direction?: ConversationDirection;
};

export type TimelineItemType = {
  conversations: TimelineConversationType[];
  daysFromStart: number;
  status: ConversationStatus;
};

type PropsT = {
  /** Extra classnames */
  className?: string;
  /** Scrollbar lines */
  scrollbarLines?: [
    {
      status: string;
      offset: number;
    },
  ];
  /** The timeline items */
  items: Array<TimelineItemType>;
  /** The start date */
  startDate: Date;
  /** The end date */
  endDate: Date;
  /** Selection callback */
  onConversationSelect: (csid: number) => void;
  /** Current csid */
  currentConversation: number;
};

type StateT = {
  firstDay: Date;
  lastDay: Date;
  daysTotal: number;
  offsetTop: number;
  showMinimap: boolean;
  firstWeekday: number;
};

const STATUS_TO_COLOR = {
  closed: colors.neutral[400],
  pending: colors.blue[400],
  awaitingpending: colors.blue[400],
  open: colors.green[400],
};

const LOCAL_DATE_OPTIONS = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

// The number of days to add in each of the timeline, to allow for the fade out effect
const PAD_DAYS = 2;

const DAY_HEIGHT = 23;

const WEEK_PADDING = 2;

const GAP_HEIGHT = 14;

const DAY_PADDING = 2;

function calculateNumberOfGaps(numberOfDays, firstWeekday) {
  const numberOfDaysInFirstWeek = 7 - firstWeekday;
  const daysAfterFirstWeek = numberOfDays - numberOfDaysInFirstWeek;
  const weeksStartedAfterFirstWeek = Math.ceil(daysAfterFirstWeek / 7);
  return weeksStartedAfterFirstWeek;
}

export class TimelineComponent extends React.PureComponent<PropsT, StateT> {
  scrollareaRef: {
    current: null | HTMLDivElement;
  };

  currentDayRef: {
    current: null | HTMLDivElement;
  };

  constructor(props: PropsT) {
    super(props);

    this.scrollareaRef = React.createRef();
    this.currentDayRef = React.createRef();

    const { startDate, endDate } = props;
    const firstDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() - PAD_DAYS,
    );

    const lastDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate() + PAD_DAYS,
    );

    const startDay = (firstDay.getDay() || 7) - 1;
    this.state = {
      firstDay,
      lastDay,
      daysTotal:
        (lastDay.valueOf() - firstDay.valueOf()) / 1000 / 60 / 60 / 24 + 1,
      offsetTop: startDay === 0 ? WEEK_PADDING : 0,
      showMinimap: false,
      firstWeekday: startDay,
    };
  }

  componentDidMount() {
    this.scrollToCurrentDay();
    this.toggleMinimap();
    window.addEventListener('resize', this.toggleMinimap);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.toggleMinimap);
  }

  toggleMinimap = () => {
    const { current: scrollArea } = this.scrollareaRef;
    if (scrollArea) {
      this.setState({
        showMinimap: scrollArea.offsetHeight < scrollArea.scrollHeight,
      });
    }
  };

  getBackgroundOffset() {
    const { firstDay } = this.state;
    // Get the weekday where 0 is monday and 6 is sunday
    const weekday = (firstDay.getDay() || 7) - 1;
    // If the first day is a monday, default to top
    if (!weekday) return 'left top';
    return `left ${-(DAY_HEIGHT * weekday + WEEK_PADDING)}px`;
  }

  getTimelineHeight() {
    const { lastDay } = this.state;
    const { offsetTop, daysTotal, firstWeekday } = this.state;
    // If the last day on the timeline is a sunday add an extra week pad
    const offsetBottom = lastDay.getDay() === 0 ? WEEK_PADDING : 0;
    // Calculate how many gaps the timeline contains
    const gaps = calculateNumberOfGaps(daysTotal, firstWeekday);
    return (
      daysTotal * DAY_HEIGHT +
      gaps * GAP_HEIGHT +
      DAY_PADDING +
      offsetBottom +
      offsetTop
    );
  }

  /**
   * Get the vertical distance from the top of the timeline for a specific day
   *
   * @param {number} daysFromStart Number of days since the first day in the
   *   timeline
   * @returns {number} Distance (in pixels)
   */
  getItemTop(daysFromStart: number) {
    const { offsetTop, firstWeekday } = this.state;
    /* daysFromStart is zero based, so add 1 and add the number of pad days,
      to get the total amount of days */
    const daysTotal = daysFromStart + 1 + PAD_DAYS;
    const gaps = calculateNumberOfGaps(daysTotal, firstWeekday);
    return (
      (daysFromStart + PAD_DAYS) * DAY_HEIGHT +
      gaps * 14 +
      DAY_PADDING +
      offsetTop
    );
  }

  scrollToCurrentDay() {
    const { current: scrollArea } = this.scrollareaRef;
    const { current: currentDay } = this.currentDayRef;
    if (scrollArea && currentDay) {
      const { top, height } = scrollArea.getBoundingClientRect();
      const currentDayBounds = currentDay.getBoundingClientRect();
      scrollArea.scrollTop = currentDayBounds.top - top - height / 2;
    }
  }

  renderTodayMarker() {
    const { daysTotal } = this.state;
    // Remove the pad days and subtract 1 as daysFromStart should be zero-based
    const todayDaysFromStart = daysTotal - 2 * PAD_DAYS - 1;
    return (
      <div
        className={styles.todayMarker}
        style={{ top: `${this.getItemTop(todayDaysFromStart)}px` }}
      />
    );
  }

  renderDateMarkers() {
    const { firstDay, lastDay } = this.state;

    // Calculate how many days until the first monday (ie. gap)
    let daysTillMonday = 8 - firstDay.getDay();
    // If firstDay is sunday daysTillMonday is now 8, make it 1
    daysTillMonday = daysTillMonday === 8 ? 1 : daysTillMonday;

    // Make a date marker and add the days till monday
    const dateMarker = new Date(firstDay.getTime());
    dateMarker.setDate(dateMarker.getDate() + daysTillMonday);

    let daysOffset = 0;
    const dateMarkers: React.ReactNode[] = [];
    let index = 0;

    // Continue until we move past the lastDay in the timeline
    while (dateMarker < lastDay) {
      // Make a human readable date
      let humanDate: string | null = null;
      try {
        humanDate = dateMarker.toLocaleDateString(
          'en-US',
          LOCAL_DATE_OPTIONS as any,
        );
      } catch (e) {
        humanDate = `${
          dateMarker.getMonth() + 1
        } / ${dateMarker.getFullYear()}`;

        console.warn('toLocaleDateString', e);
      }
      // Push the date marker
      dateMarkers.push(
        <div
          key={index}
          className={styles.dateMarker}
          // Calculate the "daysFromStart" equivalent for the current position
          style={{
            top: `${this.getItemTop(daysOffset + daysTillMonday - PAD_DAYS)}px`,
          }}
        >
          <span className={styles.dateLabel}>{humanDate}</span>
        </div>,
      );
      // Jump two weeks into the future
      dateMarker.setDate(dateMarker.getDate() + 14);
      daysOffset += 14;
      index += 1;
    }
    return <div>{dateMarkers}</div>;
  }

  render() {
    const {
      className,
      scrollbarLines,
      items,
      startDate,
      endDate,
      onConversationSelect,
      currentConversation,
      ...otherProps
    } = this.props;

    const { daysTotal, showMinimap } = this.state;

    const rootClass = classnames(
      {
        [styles.root]: true,
        [styles.rootNoMinimap]: !showMinimap,
      },
      className,
    );

    return (
      <div {...otherProps} className={rootClass}>
        <div className={styles.scrollarea} ref={this.scrollareaRef}>
          <div
            className={styles.weeks}
            style={{
              height: `${this.getTimelineHeight()}px`,
              backgroundPosition: this.getBackgroundOffset(),
            }}
          >
            {this.renderTodayMarker()}
            {items.map((item, index) => (
              <TimelineItem
                key={index}
                status={item.status}
                className={styles.item}
                conversations={item.conversations}
                onConversationSelect={onConversationSelect}
                style={{ top: this.getItemTop(item.daysFromStart) }}
                isCurrent={item.conversations
                  .map((c) => c.id)
                  .includes(currentConversation)}
                nodeRef={
                  item.conversations
                    .map((c) => c.id)
                    .includes(currentConversation)
                    ? this.currentDayRef
                    : null
                }
              />
            ))}
            {this.renderDateMarkers()}
          </div>
        </div>

        {showMinimap && (
          <div>
            <svg width="11px" height="100%" className={styles.scrollbarLines}>
              {items.map((item, index) => (
                <rect
                  key={index}
                  x={item.status === 'closed' ? '4px' : '0px'}
                  y={`${
                    ((item.daysFromStart + PAD_DAYS) / (daysTotal - 1)) * 100
                  }%`}
                  width={item.status === 'closed' ? '6px' : '12px'}
                  height="2"
                  rx="1"
                  ry="1"
                  fill={STATUS_TO_COLOR[item.status]}
                />
              ))}
            </svg>
            <Scrollbar
              container={this.scrollareaRef}
              className={styles.scrollbar}
              thumbClassName={styles.scrollbarHandle}
              trackClassName={styles.scrollBarTrack}
            />
          </div>
        )}
      </div>
    );
  }
}
